import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import { toast } from "sonner";
import { 
  CheckCircle2, 
  BookOpen, 
  Clock, 
  Shield, 
  ArrowRight,
  Star,
  Loader2
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FEATURED_COVER = "https://customer-assets.emergentagent.com/job_omw-academy/artifacts/ljfzsvtw_IMG_5405.png";

const PromoPage = () => {
  const [purchasing, setPurchasing] = useState(false);
  const { user, getAuthHeader } = useAuth();
  const navigate = useNavigate();

  const handlePurchase = async () => {
    if (!user) {
      toast.info("Please sign in to purchase");
      navigate("/login", { state: { from: "/promo" } });
      return;
    }

    setPurchasing(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/payments/checkout`,
        {
          ebook_id: "ebook-1",
          origin_url: window.location.origin
        },
        { headers: getAuthHeader() }
      );

      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error("Error creating checkout:", error);
      toast.error("Unable to process purchase. Please try again.");
    } finally {
      setPurchasing(false);
    }
  };

  const whatYoullLearn = [
    "The 60/40 Principle - A proven budget system that builds wealth automatically",
    "How to discover and monetize your God-given gifts and talents",
    "The 5-Phase Transition Plan to escape the 9-to-5 grind",
    "7 types of income streams and how to start building yours today",
    "Smart money decisions that protect and grow your wealth",
    "Step-by-step action plans with worksheets and checklists"
  ];

  const bonuses = [
    "Transition Milestone Worksheet",
    "60/40 Budget Calculator Template",
    "Gift-to-Income Assessment Guide",
    "Wealth Protection Checklist"
  ];

  return (
    <div className="min-h-screen bg-white" data-testid="promo-page">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#1E3A5F] via-[#162c47] to-[#0f1e32] py-16 lg:py-24">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left - Text */}
            <div className="text-center lg:text-left">
              <div className="inline-flex items-center gap-2 bg-red-600 text-white px-4 py-2 rounded-full text-sm font-bold mb-6 animate-pulse">
                <Clock className="h-4 w-4" />
                LIMITED TIME: 50% OFF!
              </div>
              
              <h1 className="text-4xl lg:text-5xl font-bold text-white mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
                Stop Living Paycheck to Paycheck
              </h1>
              
              <p className="text-xl text-[#9D9045] font-semibold mb-4">
                Real Solutions to Build Wealth
              </p>
              
              <p className="text-lg text-gray-300 mb-8">
                Discover the proven system that helped me leave the corporate world and build financial freedom through purpose. 
                This isn't about cutting coupons—it's about transforming your entire relationship with money.
              </p>

              <div className="flex flex-col sm:flex-row items-center gap-4 mb-6">
                <div className="flex items-center gap-3">
                  <span className="text-3xl text-gray-400 line-through">$19.99</span>
                  <span className="text-5xl font-bold text-white">$9.99</span>
                </div>
                <span className="bg-red-600 text-white px-3 py-1 rounded-full text-sm font-bold">
                  SAVE 50%
                </span>
              </div>

              <Button 
                size="lg"
                className="w-full sm:w-auto bg-[#9D9045] hover:bg-[#8a7e3c] text-white px-10 py-6 text-xl font-bold shadow-xl"
                onClick={handlePurchase}
                disabled={purchasing}
                data-testid="promo-buy-btn"
              >
                {purchasing ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    Get Instant Access
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </>
                )}
              </Button>

              <p className="text-gray-400 text-sm mt-4 flex items-center justify-center lg:justify-start gap-2">
                <Shield className="h-4 w-4" />
                Secure checkout • Instant digital delivery
              </p>
            </div>

            {/* Right - Book Image */}
            <div className="relative flex justify-center">
              <img 
                src={FEATURED_COVER}
                alt="Stop Living Paycheck to Paycheck Ebook"
                className="w-full max-w-sm rounded-lg shadow-2xl transform hover:scale-105 transition-transform duration-300"
              />
              <div className="absolute -top-4 -right-4 bg-red-600 text-white w-20 h-20 rounded-full flex items-center justify-center font-bold text-lg shadow-lg">
                50%<br/>OFF
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Social Proof */}
      <section className="bg-[#9D9045] py-4">
        <div className="max-w-6xl mx-auto px-6 text-center">
          <div className="flex flex-wrap justify-center items-center gap-8 text-white">
            <div className="flex items-center gap-2">
              <BookOpen className="h-5 w-5" />
              <span className="font-semibold">4,700+ Words of Actionable Content</span>
            </div>
            <div className="flex items-center gap-2">
              <Star className="h-5 w-5 fill-current" />
              <span className="font-semibold">Worksheets & Templates Included</span>
            </div>
            <div className="flex items-center gap-2">
              <Shield className="h-5 w-5" />
              <span className="font-semibold">Veteran-Owned Business</span>
            </div>
          </div>
        </div>
      </section>

      {/* What You'll Learn */}
      <section className="py-16 lg:py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <h2 className="text-3xl lg:text-4xl font-bold text-[#1E3A5F] text-center mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
            What You'll Learn
          </h2>
          <p className="text-gray-600 text-center mb-12 text-lg">
            Practical strategies you can implement today
          </p>

          <div className="space-y-4">
            {whatYoullLearn.map((item, index) => (
              <div 
                key={index}
                className="flex items-start gap-4 bg-white p-5 rounded-xl shadow-sm"
              >
                <CheckCircle2 className="h-6 w-6 text-green-600 flex-shrink-0 mt-0.5" />
                <span className="text-gray-700 text-lg">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About the Author */}
      <section className="py-16 lg:py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <div className="bg-[#1E3A5F] rounded-2xl p-8 lg:p-12 text-white">
            <h2 className="text-2xl lg:text-3xl font-bold mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
              About the Author
            </h2>
            <p className="text-lg text-gray-200 mb-4">
              <strong className="text-[#9D9045]">Lavista Smith</strong> is the founder of OMW Business & Financial Resources, 
              a Veteran-Owned Small Business based in Alabama.
            </p>
            <p className="text-lg text-gray-200 mb-4">
              With <strong>over a decade in banking</strong> and a combined <strong>25+ years of expertise</strong> in 
              financial services, money management, and military service, Lavista brings real-world experience to every page.
            </p>
            <p className="text-lg text-gray-200">
              This isn't theory from someone who read about money in a textbook. This is hard-won wisdom from someone 
              who made the transition from paycheck-dependent to purpose-driven—and wants to show you how to do the same.
            </p>
          </div>
        </div>
      </section>

      {/* Bonuses */}
      <section className="py-16 lg:py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <span className="text-[#9D9045] font-semibold text-sm tracking-wider uppercase">Included Free</span>
          <h2 className="text-3xl lg:text-4xl font-bold text-[#1E3A5F] mt-2 mb-12" style={{ fontFamily: 'Playfair Display, serif' }}>
            Bonus Resources
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {bonuses.map((bonus, index) => (
              <div 
                key={index}
                className="bg-white p-6 rounded-xl shadow-sm border-2 border-[#9D9045]/20 flex items-center gap-4"
              >
                <div className="bg-[#9D9045]/10 p-3 rounded-full">
                  <CheckCircle2 className="h-6 w-6 text-[#9D9045]" />
                </div>
                <span className="text-[#1E3A5F] font-semibold text-left">{bonus}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 lg:py-24 bg-gradient-to-br from-[#1E3A5F] via-[#162c47] to-[#0f1e32]">
        <div className="max-w-3xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
            Ready to Break Free?
          </h2>
          <p className="text-xl text-gray-300 mb-8">
            Stop trading your time for just enough to get by. Start building wealth with purpose.
          </p>

          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 mb-8">
            <div className="flex items-center justify-center gap-4 mb-6">
              <span className="text-2xl text-gray-400 line-through">$19.99</span>
              <span className="text-5xl font-bold text-white">$9.99</span>
              <span className="bg-red-600 text-white px-3 py-1 rounded-full text-sm font-bold">
                50% OFF
              </span>
            </div>
            
            <Button 
              size="lg"
              className="w-full sm:w-auto bg-[#9D9045] hover:bg-[#8a7e3c] text-white px-12 py-6 text-xl font-bold shadow-xl"
              onClick={handlePurchase}
              disabled={purchasing}
              data-testid="promo-buy-btn-bottom"
            >
              {purchasing ? (
                <>
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  Get Your Copy Now
                  <ArrowRight className="ml-2 h-5 w-5" />
                </>
              )}
            </Button>
          </div>

          <p className="text-gray-400 text-sm flex items-center justify-center gap-2">
            <Shield className="h-4 w-4" />
            Secure payment via Stripe • Instant access after purchase
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0f1e32] py-8 text-center">
        <p className="text-gray-400 text-sm">
          © 2024 OMW Business & Financial Resources. All Rights Reserved.
        </p>
        <p className="text-gray-500 text-xs mt-2">
          Veteran-Owned Small Business | omwbusiness.com
        </p>
      </footer>
    </div>
  );
};

export default PromoPage;
